import React, { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Select, MenuItem, InputLabel, FormControl } from '@mui/material';
import { SearchTextField } from '../../../forms/search/search-text-field';
import { ChoicesStatusMilestone } from '@/types';
import Grid from '@mui/material/Unstable_Grid2';
import { IBillingFilters } from '../types';

interface IMonthOption {
  value: number;
  label: string;
}

interface IFiltersBarProps {
  filters: IBillingFilters;
  setFilters: React.Dispatch<React.SetStateAction<IBillingFilters>>;
  yearOptions: number[];
  monthOptions: IMonthOption[];
}

export function FiltersBar({ filters, setFilters, yearOptions, monthOptions }: IFiltersBarProps) {
  const { t } = useTranslation();

  const statusOptions = useMemo(
    () => [
      { _id: ChoicesStatusMilestone.SCHEDULED, label: t('Schedule') },
      { _id: ChoicesStatusMilestone.OPEN_TO_BILL, label: t('Open to Bill') },
      { _id: ChoicesStatusMilestone.PAST_DUE, label: t('Past Due') },
    ],
    [t]
  );

  const handleFilterChange = useCallback(
    (key: keyof IBillingFilters, parseFn: (v: string) => any = (v) => v) =>
      (event: { target: { value: string } }) => {
        const { value } = event.target;
        setFilters((prev) => ({
          ...prev,
          [key]: value ? [parseFn(value)] : [],
        }));
      },
    [setFilters]
  );

  return (
    <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
      <Grid md={8}>
        <SearchTextField filters={filters} setFilters={setFilters} sx={{ minWidth: 250 }} />
      </Grid>
      <Grid md={1.2}>
        <FormControl size="small" sx={{ width: '100%' }}>
          <InputLabel>{t('Year')}</InputLabel>
          <Select
            value={filters.year.length > 0 ? String(filters.year[0]) : ''}
            label={t('Year')}
            onChange={handleFilterChange('year', (v) => parseInt(v, 10))}
          >
            <MenuItem value="">{t('Every Year')}</MenuItem>
            {yearOptions.map((year) => (
              <MenuItem key={year} value={year}>
                {year}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid md={1.2}>
        <FormControl size="small" sx={{ width: '100%' }}>
          <InputLabel>{t('Month')}</InputLabel>
          <Select
            value={filters.month.length > 0 ? String(filters.month[0]) : ''}
            label={t('Month')}
            onChange={handleFilterChange('month', (v) => parseInt(v, 10))}
          >
            <MenuItem value="">{t('Every Month')}</MenuItem>
            {monthOptions.map((month) => (
              <MenuItem key={month.value} value={month.value}>
                {month.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid md={1.6}>
        <FormControl size="small" sx={{ width: '100%' }}>
          <InputLabel>{t('Status')}</InputLabel>
          <Select
            value={filters.status.length > 0 ? filters.status[0] : ''}
            label={t('Status')}
            onChange={handleFilterChange('status')}
          >
            <MenuItem value="">{t('Every Status')}</MenuItem>
            {statusOptions.map((option) => (
              <MenuItem key={option._id} value={option._id}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
    </Grid>
  );
}
